import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTextosDaLoja } from '@/construtor/textos';
import { localeOf, money } from '@/lib/partner/format';
import type { PartnerDailyPoint } from '@/lib/services/partner.service';

/*
 * Barras por dia, uma série para o valor vendido e outra para a comissão, no
 * mesmo eixo em reais. Cores dos slots 1 e 2 da paleta categórica validada
 * (daltonismo e contraste), com um passo próprio para o tema escuro da loja.
 */
const CORES =
  '[--color-revenue:#2a78d6] [--color-commission:#eb6834] dark:[--color-revenue:#3987e5] dark:[--color-commission:#d95926]';

export function PartnerSalesChart({ daily }: { daily: PartnerDailyPoint[] }) {
  const { t, i18n } = useTextosDaLoja();
  const language = i18n.language;
  const locale = localeOf(language);

  if (daily.every((d) => d.sales === 0)) {
    return (
      <div className="flex h-[260px] items-center justify-center rounded-lg border-2 border-dashed border-border text-sm text-muted-foreground">
        {t('store.partner.chart_empty')}
      </div>
    );
  }

  const dia = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
  const eixo = new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL', notation: 'compact', maximumFractionDigits: 1 });

  const series = [
    { key: 'revenue' as const, label: t('store.partner.chart_revenue') },
    { key: 'commission' as const, label: t('store.partner.chart_commission') },
  ];

  return (
    <div className={`space-y-3 ${CORES}`}>
      <ul className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        {series.map((s) => (
          <li key={s.key} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: `var(--color-${s.key})` }} />
            {s.label}
          </li>
        ))}
      </ul>
      <div className="h-[260px] w-full text-xs">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={daily} margin={{ top: 8, right: 8, left: 4, bottom: 0 }} barGap={2} barCategoryGap="20%">
            <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border" />
            <XAxis
              dataKey="date"
              tickFormatter={dia}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis
              tickFormatter={(v: number) => eixo.format(v / 100)}
              tickLine={false}
              axisLine={false}
              width={64}
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const ponto = payload[0].payload as PartnerDailyPoint;
                return (
                  <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
                    <p className="mb-1 font-medium text-foreground">{dia(String(label))}</p>
                    {series.map((s) => (
                      <p key={s.key} className="flex items-center gap-2 text-foreground">
                        <span className="h-2 w-2 rounded-full" style={{ background: `var(--color-${s.key})` }} />
                        {s.label}: <span className="font-medium">{money(ponto[s.key], language)}</span>
                      </p>
                    ))}
                    <p className="mt-1 text-muted-foreground">{t('store.partner.chart_sales', { count: ponto.sales })}</p>
                  </div>
                );
              }}
            />
            {series.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                fill={`var(--color-${s.key})`}
                radius={[4, 4, 0, 0]}
                maxBarSize={18}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
